import { type IChartingLibraryWidget } from '@frontends/feature-charting-library/charting_library';
import { type SupportedLanguage } from '@frontends/util-i18n';
import { type RefObject, useEffect, useRef, useState } from 'react';

export type ChartLanguageOptions = {
  appLanguage: SupportedLanguage;
  isSessionInProgress: boolean;
  readyWidget: IChartingLibraryWidget | null;
};

export type ChartLanguage = {
  chartLanguage: SupportedLanguage;
  savedLayoutRef: RefObject<object | null>;
};

export function useChartLanguage({
  appLanguage,
  isSessionInProgress,
  readyWidget,
}: ChartLanguageOptions): ChartLanguage {
  const [chartLanguage, setChartLanguage] = useState(appLanguage);
  const savedLayoutRef = useRef<object | null>(null);

  useEffect(() => {
    if (readyWidget) {
      savedLayoutRef.current = null;
    }
  }, [readyWidget]);

  useEffect(() => {
    if (chartLanguage === appLanguage || isSessionInProgress) {
      return;
    }

    if (!readyWidget) {
      setChartLanguage(appLanguage);

      return;
    }

    let isCancelled = false;

    try {
      readyWidget.save((layout) => {
        if (isCancelled) {
          return;
        }

        savedLayoutRef.current = layout;
        setChartLanguage(appLanguage);
      });
    } catch (error) {
      console.warn('Chart layout save failed', error);
      setChartLanguage(appLanguage);
    }

    return () => {
      isCancelled = true;
    };
  }, [appLanguage, chartLanguage, isSessionInProgress, readyWidget]);

  return { chartLanguage, savedLayoutRef };
}
